import getEnv from '@/utils/getEnv';
import type { AssetClassConstants } from '@/constants/AssetClassConstants';
import { TimeSeriesEntityTypeConstants } from '@/constants/TimeSeriesEntityTypeConstants';
import { TrackTypeConstants } from '@/constants/TrackTypeConstants';
import { PortfolioType } from './portfolio';

export enum DataType {
  PURE_FACTOR = 'PURE_FACTOR',

  /**
   * Corresponds to:
   * - TimeSeriesEntityTypeConstants.STRATEGY
   * - "QIS" on BofA
   */
  STRATEGY = 'STRATEGY',

  /**
   * Corresponds to:
   * - TimeSeriesEntityTypeConstants.PRIVATE_STRATEGY
   * - "My QIS" on BofA
   */
  PRIVATE_STRATEGY = 'PRIVATE_STRATEGY',
  FUND = 'FUND',

  /**
   * Corresponds to:
   * - TimeSeriesEntityTypeConstants.PUBLIC_THEMATIC
   * - "Custom Baskets" on BofA
   */
  THEMATIC = 'THEMATIC',

  /**
   * Corresponds to:
   * - TimeSeriesEntityTypeConstants.THEMATIC,
   * - "My Custom Baskets" on BofA
   */
  PRIVATE_THEMATIC = 'PRIVATE_THEMATIC',
  PRIVATE_TRACK = 'PRIVATE_TRACK',
  SIGNAL = 'SIGNAL',
  MARKET_DATA = 'MARKET_DATA',
  STOCK = 'STOCK',
  CASH = 'CASH',

  STRUCTURED_PRODUCT = 'STRUCTURED_PRODUCT',

  // Portfolio Types
  PEER_GROUP = 'PEER_GROUP',
  BENCHMARK = 'BENCHMARK',
  PORTFOLIO = 'PORTFOLIO',
  EQUITY_BASKET = 'EQUITY_BASKET',
  QIS_BASKET = 'QIS_BASKET',
}

export type StrategyLikeObject = {
  type: TimeSeriesEntityTypeConstants;
  trackType: TrackTypeConstants;
  assetClass: AssetClassConstants;
};

// The display type depends on both the type and the trackType property.
// We use the display type for client display only. For admin console, we display the original type.
// Users will be able to customize the display type for their own private tracks.
export const normalizeType = (o: StrategyLikeObject): DataType => {
  const { isBofAEnvironment } = getEnv();
  if (o.trackType === TrackTypeConstants.PRIVATE_TRACK || o.trackType === TrackTypeConstants.PROXY) {
    if (o.type === null || o.type === TimeSeriesEntityTypeConstants.PRIVATE_STRATEGY) {
      return DataType.PRIVATE_TRACK;
    }
  }

  // In BofA environment, we are separating the thematic and strategy types according to their visibility.
  if (isBofAEnvironment) {
    switch (o.type) {
      case TimeSeriesEntityTypeConstants.STRATEGY:
        return DataType.STRATEGY;
      case TimeSeriesEntityTypeConstants.PRIVATE_STRATEGY:
        return DataType.PRIVATE_STRATEGY;
      case TimeSeriesEntityTypeConstants.THEMATIC:
        return DataType.PRIVATE_THEMATIC;
      case TimeSeriesEntityTypeConstants.PUBLIC_THEMATIC:
        return DataType.THEMATIC;
      case TimeSeriesEntityTypeConstants.PURE_FACTOR:
        return DataType.PURE_FACTOR;
      case TimeSeriesEntityTypeConstants.BENCHMARK:
        return DataType.BENCHMARK;
      case TimeSeriesEntityTypeConstants.PUBLIC_FUND:
      case TimeSeriesEntityTypeConstants.PRIVATE_FUND:
      case TimeSeriesEntityTypeConstants.FUND:
        return DataType.FUND;
      case TimeSeriesEntityTypeConstants.PURE_FACTOR_SIGNAL:
      case TimeSeriesEntityTypeConstants.SIGNAL:
        return DataType.SIGNAL;
      case TimeSeriesEntityTypeConstants.MARKET_DATA:
        return DataType.MARKET_DATA;
      case TimeSeriesEntityTypeConstants.STOCK:
        return DataType.STOCK;
      case TimeSeriesEntityTypeConstants.CASH:
        return DataType.CASH;
      case TimeSeriesEntityTypeConstants.STRUCTURED_PRODUCT:
        return DataType.STRUCTURED_PRODUCT;
      default:
        throw new Error('Unreachable: Unrecognized strategy');
    }
  }

  switch (o.type) {
    case TimeSeriesEntityTypeConstants.STRATEGY:
    case TimeSeriesEntityTypeConstants.PRIVATE_STRATEGY:
      return DataType.STRATEGY;
    case TimeSeriesEntityTypeConstants.THEMATIC:
    case TimeSeriesEntityTypeConstants.PUBLIC_THEMATIC:
    case TimeSeriesEntityTypeConstants.THEMATIC_BENCHMARK:
      return DataType.THEMATIC;
    case TimeSeriesEntityTypeConstants.PURE_FACTOR:
      return DataType.PURE_FACTOR;
    case TimeSeriesEntityTypeConstants.BENCHMARK:
      return DataType.BENCHMARK;
    case TimeSeriesEntityTypeConstants.PUBLIC_FUND:
    case TimeSeriesEntityTypeConstants.PRIVATE_FUND:
    case TimeSeriesEntityTypeConstants.FUND:
      return DataType.FUND;
    case TimeSeriesEntityTypeConstants.PURE_FACTOR_SIGNAL:
    case TimeSeriesEntityTypeConstants.SIGNAL:
      return DataType.SIGNAL;
    case TimeSeriesEntityTypeConstants.MARKET_DATA:
      return DataType.MARKET_DATA;
    case TimeSeriesEntityTypeConstants.STOCK:
      return DataType.STOCK;
    case TimeSeriesEntityTypeConstants.CASH:
      return DataType.CASH;
    case TimeSeriesEntityTypeConstants.STRUCTURED_PRODUCT:
      return DataType.STRUCTURED_PRODUCT;
    default:
      throw new Error('Unreachable: Unrecognized strategy');
  }
};

export const normalizePortfolioType = (o: {
  type: PortfolioType;
  isBenchmark: boolean;
}): DataType.BENCHMARK | DataType.PEER_GROUP | DataType.PORTFOLIO | DataType.EQUITY_BASKET | DataType.QIS_BASKET => {
  if (o.type === PortfolioType.EQUITY_BASKET) return DataType.EQUITY_BASKET;

  if (o.type === PortfolioType.QIS_BASKET) return DataType.QIS_BASKET;

  if (o.type === PortfolioType.PEER_GROUP) return DataType.PEER_GROUP;

  return o.isBenchmark ? DataType.BENCHMARK : DataType.PORTFOLIO;
};

export interface IColors {
  [aggregationField: string]: { [key: string]: string } | undefined;
}

export interface ISettingRecord {
  id: number;
  key: string;
  value: string;
  aggregationField: string;
}
